import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../lib/AuthContext";
import { CircleNotch, TrendUp, UserPlus, CalendarBlank } from "@phosphor-icons/react";

export default function AdminSignups() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/signups?period=${period}&page=${page}&per_page=25`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, period, page]);

  useEffect(() => { load(); }, [load]);

  const s = data?.stats || {};

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-signups">
      <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">New Signups</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Today" value={s.today} icon={UserPlus} color="text-teal" bg="bg-teal/10" />
        <StatCard label="This Week" value={s.week} icon={CalendarBlank} color="text-blue-600" bg="bg-blue-50" />
        <StatCard label="This Month" value={s.month} icon={CalendarBlank} color="text-violet-600" bg="bg-violet-50" />
        <StatCard label="WoW Growth" value={`${s.wow_growth || 0}%`} icon={TrendUp} color="text-emerald-600" bg="bg-emerald-50" />
      </div>

      {/* Period filter */}
      <div className="flex items-center gap-2 mb-4">
        {["all", "today", "week", "month"].map(p => (
          <button key={p} onClick={() => { setPeriod(p); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${period === p ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {p === "all" ? "All Time" : p === "today" ? "Today" : p === "week" ? "This Week" : "This Month"}
          </button>
        ))}
      </div>

      {/* Signups list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
        ) : (data?.users || []).length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">No signups found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.users.map(u => (
              <div key={u.auth0_id} className="flex items-center justify-between px-5 py-4 hover:bg-slate-50/50">
                <div>
                  <p className="font-medium text-slate-900">{u.name || "No name"}</p>
                  <p className="text-sm text-slate-500">{u.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</p>
                  <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${u.tier === "free" ? "bg-slate-100 text-slate-600" : "bg-teal/10 text-teal-700"}`}>{u.tier}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>Page {data.page} of {data.total_pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-40">Prev</button>
            <button disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`w-4.5 h-4.5 ${color}`} weight="fill" /></div>
        <div><p className="text-xl font-bold text-slate-900">{value ?? "—"}</p><p className="text-xs text-slate-500">{label}</p></div>
      </div>
    </div>
  );
}
